/** How names and offenses read in dense records tables. */
import type { OffenseDef } from '@civilos/domain';

const SMALL = new Set(['de', 'del', 'la', 'van', 'von', 'da', 'di']);
const cap = (w: string) =>
  w
    .toLowerCase()
    .replace(/(^|[-'’])(\p{L})/gu, (_m, a: string, b: string) => a + b.toUpperCase())
    .replace(/^Mc(\p{L})/u, (_m, b: string) => 'Mc' + b.toUpperCase());

/** "BRANDON R. WHITMORE" / "Brandon R. Whitmore" → "Whitmore, Brandon R." */
export function lastFirst(n: string | null | undefined): string {
  if (!n) return '';
  const tidy = (s: string) =>
    s
      .trim()
      .split(/\s+/)
      .map((w, i) => (i > 0 && SMALL.has(w.toLowerCase()) ? w.toLowerCase() : /^(ii|iii|iv)$/i.test(w) ? w.toUpperCase() : cap(w)))
      .join(' ');
  if (n.includes(',')) {
    const [last, ...rest] = n.split(',');
    return `${tidy(last!)}, ${tidy(rest.join(','))}`.replace(/,\s*$/, '');
  }
  const parts = n.trim().split(/\s+/);
  if (parts.length < 2) return tidy(n);
  const suffix = /^(jr\.?|sr\.?|ii|iii|iv)$/i.test(parts.at(-1)!) ? parts.pop()! : '';
  const last = parts.pop()!;
  return `${tidy(last)}${suffix ? ' ' + tidy(suffix) : ''}, ${tidy(parts.join(' '))}`;
}

/** "Deputy Shay Pryor" → "S. Pryor". */
export function officerShort(n: string | null | undefined): string {
  if (!n) return '';
  const p = n
    .replace(/^(Deputy|Dep\.|Sgt\.|Lt\.|Cpl\.|Capt\.|Sheriff|Officer|Investigator|Inv\.)\s+/i, '')
    .trim()
    .split(/\s+/);
  return p.length > 1 ? `${p[0]![0]}. ${p.at(-1)}` : n;
}

/** "Aggravated assault (weapon or serious injury)" → "Aggravated assault": the parenthetical helps when picking, not when scanning a list. */
export function offenseShort(def: Pick<OffenseDef, 'plain'> | undefined | null, fallback = ''): string {
  if (!def) return fallback;
  return def.plain.replace(/\s*\([^)]*\)\s*$/, '');
}
